import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Inbox, ArrowRight, Clock, AlertTriangle, FileText, Film, Smartphone, Mail, Video } from "lucide-react";

const KIND_ICONS = {
  brief: FileText,
  script: Film,
  push: Smartphone,
  email: Mail,
  video_edit: Video,
};
const KIND_LABELS = {
  brief: "Brief",
  script: "Guion",
  push: "Push",
  email: "Email",
  video_edit: "Video",
};
const PRIORITY_COLOR = {
  urgente: "bg-red-100 text-red-700 border-red-300",
  alta: "bg-orange-100 text-orange-700 border-orange-300",
  media: "bg-blue-50 text-blue-700 border-blue-200",
  baja: "bg-gray-50 text-gray-600 border-gray-200",
};

const Approvals = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/command/approvals").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const items = data?.items || [];
  const filtered = filter === "all" ? items : items.filter((i) => i.kind === filter);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Layout
      title="Bandeja de Aprobaciones"
      subtitle="Todo lo que requiere tu decisión · sin salir de aquí"
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <>
          {/* Stats top */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5">
            <StatCard label="Total" value={data?.total || 0} highlight onClick={() => setFilter("all")} active={filter === "all"} />
            {Object.entries(data?.by_kind || {}).map(([k, v]) => (
              <StatCard key={k} label={KIND_LABELS[k] || k} value={v} icon={KIND_ICONS[k]} onClick={() => setFilter(k)} active={filter === k} />
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="aura-card-flat p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-green-100 text-green-700 flex items-center justify-center mx-auto mb-4">
                <Inbox size={24} />
              </div>
              <h3 className="font-display text-2xl font-medium">¡Bandeja vacía!</h3>
              <p className="text-[#666] mt-2">No tienes nada pendiente de aprobación. Crack.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="approvals-list">
              {filtered.map((it) => {
                const Icon = KIND_ICONS[it.kind] || FileText;
                const overdue = it.due_date && it.due_date < today;
                return (
                  <Link key={`${it.kind}-${it.id}`} to={it.deep_link} className="block aura-card p-4 hover:border-[#D99D26] transition-all" data-testid={`approval-${it.id}`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${PRIORITY_COLOR[it.priority] || PRIORITY_COLOR.media} border`}>
                        <Icon size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`pill text-[10px] ${PRIORITY_COLOR[it.priority] || PRIORITY_COLOR.media} border`}>{it.priority}</span>
                          <span className="pill pill-gray text-[10px]">{KIND_LABELS[it.kind]}</span>
                          {it.highlight && <span className="pill pill-yellow text-[10px]">⚠️ {it.highlight}</span>}
                          {overdue && <span className="pill pill-red text-[10px] flex items-center gap-0.5"><AlertTriangle size={9} /> Vencida</span>}
                        </div>
                        <h3 className="font-display text-base font-medium leading-tight truncate">{it.title}</h3>
                        <p className="text-xs text-[#666] mt-1 truncate">{it.subtitle}</p>
                        {it.due_date && (
                          <div className="text-[11px] text-[#666] mt-1 flex items-center gap-1">
                            <Clock size={10} /> Entrega {new Date(it.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
                          </div>
                        )}
                      </div>
                      <ArrowRight size={18} className="text-[#666] flex-shrink-0 mt-3" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </Layout>
  );
};

const StatCard = ({ label, value, icon: Icon, highlight, onClick, active }) => (
  <button
    onClick={onClick}
    className={`aura-card-flat p-3 text-left transition-all ${active ? "ring-2 ring-[#D99D26]" : ""} ${highlight ? "bg-gradient-to-br from-[#FFF9E6] to-white" : ""}`}
  >
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase tracking-wider text-[#666] font-medium">{label}</span>
      {Icon && <Icon size={12} className="text-[#8a5e10]" />}
    </div>
    <div className="font-display text-2xl font-medium">{value}</div>
  </button>
);

export default Approvals;
